import { del, get, post, put } from "../../../core/api/client";
import { API_ENDPOINTS } from "../../../core/api/endpoints";

const sanitizeLimit = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 20;
  return Math.min(100, Math.max(1, Math.trunc(parsed)));
};

const sanitizeOffset = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.trunc(parsed));
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n", ""].includes(normalized)) return false;
  }
  return Boolean(value);
};

const extractClientRecord = (response) => {
  if (!response || response.error) return null;
  if (response?.client && typeof response.client === "object") return response.client;
  if (Array.isArray(response?.clients) && response.clients[0]) return response.clients[0];
  if (response?.id || response?.client_id) return response;
  return null;
};

const normalizeWhitelistedIp = (record = {}) => ({
  id: Number(record.id || 0) || null,
  client_id: Number(record.client_id || 0) || null,
  ip_address: String(record.ip_address || "").trim(),
  created_at: record.created_at || record.createdAt || "",
  updated_at: record.updated_at || record.updatedAt || "",
});

const extractWhitelistedIpList = (response) => {
  if (!response || response.error) return [];

  if (response?.data && typeof response.data === "object") {
    return extractWhitelistedIpList(response.data);
  }

  if (Array.isArray(response?.whitelistedIps)) return response.whitelistedIps;
  if (Array.isArray(response?.whitelistedIPs)) return response.whitelistedIPs;
  if (Array.isArray(response?.whitelistIps)) return response.whitelistIps;
  if (Array.isArray(response?.whitelisted_ips)) return response.whitelisted_ips;
  if (Array.isArray(response?.ips)) return response.ips;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

const buildClientUpdatePayload = (client, { pbxTagFormat, tlsEncryptionEnabled }) => ({
  client_name: client?.client_name || "",
  plan_id: Number(client?.plan_id || 0),
  client_email: client?.client_email || "",
  client_phone: client?.client_phone || "",
  client_address: client?.client_address || "",
  timezone: client?.timezone || "UTC",
  client_status: client?.client_status || "active",
  delivery_failure_notification: normalizeBoolean(client?.delivery_failure_notification),
  usage_alert_notification: normalizeBoolean(client?.usage_alert_notification),
  system_alert_notification: normalizeBoolean(client?.system_alert_notification),
  client_referral_link: client?.client_referral_link || client?.referral_link || "",
  pbx_tag_format: String(pbxTagFormat || ""),
  tls_encryption_enabled: normalizeBoolean(tlsEncryptionEnabled),
});

export const getClientPbxSettings = async (clientId) => {
  const response = await get(API_ENDPOINTS.CLIENTS.GET_BY_ID(clientId));

  if (response?.error) {
    return response;
  }

  const client = extractClientRecord(response);
  if (!client) {
    return { error: { message: "Client not found." } };
  }

  return {
    client: {
      id: Number(client.id || client.client_id || clientId),
      pbx_tag_format: String(client.pbx_tag_format || ""),
      tls_encryption_enabled: normalizeBoolean(client.tls_encryption_enabled),
      raw: client,
    },
  };
};

export const updateClientPbxSettings = async (
  clientId,
  sourceClient,
  { pbxTagFormat, tlsEncryptionEnabled },
) => {
  const payload = buildClientUpdatePayload(sourceClient, {
    pbxTagFormat,
    tlsEncryptionEnabled,
  });

  return put(API_ENDPOINTS.CLIENTS.UPDATE(clientId), payload);
};

export const parseClientEmail = async ({ client_id, email_body, delimiter }) => {
  return post(API_ENDPOINTS.CLIENTS.PARSER, {
    client_id: Number(client_id),
    email_body: String(email_body || ""),
    delimiter: String(delimiter || ""),
  });
};

export const getWhitelistedIpsByClient = async (clientId, params = {}) => {
  const safeLimit = sanitizeLimit(params.limit ?? 100);
  const safeOffset = sanitizeOffset(params.offset ?? 0);

  const response = await get(
    API_ENDPOINTS.WHITELISTED_IPS.LIST_BY_CLIENT(clientId, safeLimit, safeOffset),
  );

  if (response?.error) {
    return response;
  }

  const whitelistedIps = extractWhitelistedIpList(response)
    .map(normalizeWhitelistedIp)
    .filter((item) => item.ip_address);

  return {
    whitelistedIps,
    total: Number(response?.total || whitelistedIps.length),
    limit: Number(response?.limit || safeLimit),
    offset: Number(response?.offset || safeOffset),
  };
};

export const createWhitelistedIp = async ({ client_id, ip_address }) => {
  return post(API_ENDPOINTS.WHITELISTED_IPS.CREATE, {
    client_id: Number(client_id),
    ip_address: String(ip_address || "").trim(),
  });
};

export const getWhitelistedIpById = async (id) => {
  return get(API_ENDPOINTS.WHITELISTED_IPS.GET_BY_ID(id));
};

export const updateWhitelistedIpById = async (id, payload) => {
  return put(API_ENDPOINTS.WHITELISTED_IPS.UPDATE_BY_ID(id), payload);
};

export const deleteWhitelistedIpById = async (id) => {
  return del(API_ENDPOINTS.WHITELISTED_IPS.DELETE_BY_ID(id));
};
